import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { InfoRow, SectionCard } from '../components/common/ui';
import CompanyService from '../services/CompanyService';
import { errorMessage, fieldErrors } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { refreshCompanyLogo, useCompanyLogo } from '../components/common/companyLogo';

/** Which fields each edit panel shows (the save always sends the whole profile). */
const SECTIONS = {
    profile: ['name', 'email', 'phone', 'website'],
    details: ['address', 'city', 'taxNumber'],
    cadastre: ['cadastreReminderDays'],
    bank: ['bankName', 'bankAccount'],
};

function ContactLine({ icon, value, href }) {
    if (!value) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#475569', minWidth: 0 }}>
            <Box sx={{ display: 'flex', color: '#94A3B8' }}>{icon}</Box>
            {href
                ? <Box component="a" href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                       sx={{ fontSize: 14, color: '#427898', fontWeight: 600, textDecoration: 'none', overflowWrap: 'anywhere' }}>{value}</Box>
                : <Typography sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>{value}</Typography>}
        </Box>
    );
}

/**
 * The firm's profile, Plankton style: a profile card (logo, name, contact) and read-only sections that each open
 * their own edit panel. The logo also appears on printed quotes and forms.
 */
export default function CompanyPage() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { updateUser } = useAuth();
    const logo = useCompanyLogo();
    const [company, setCompany] = useState(null);
    const [panel, setPanel] = useState(null);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [logoBusy, setLogoBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        CompanyService.get().then(setCompany).catch(() => showToast(t('common.loadError'), 'error'));
    }, [showToast, t]);

    const open = (name) => {
        setForm(Object.fromEntries(Object.entries(company).map(([k, v]) => [k, v ?? ''])));
        setErrors({});
        setPanel(name);
    };
    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); if (errors[k]) setErrors(x => ({ ...x, [k]: undefined })); };

    const save = async (e) => {
        e?.preventDefault();
        if (!String(form.name).trim()) { setErrors({ name: t('auth.register.errors.companyRequired') }); return; }
        setSaving(true);
        try {
            const saved = await CompanyService.update({ ...form, cadastreReminderDays: form.cadastreReminderDays === '' ? null : Number(form.cadastreReminderDays) });
            setCompany(saved);
            updateUser({ companyName: saved.name });
            showToast(t('company.saved'), 'success');
            setPanel(null);
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) setErrors(fe); else showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        setLogoBusy(true);
        try {
            setCompany(await CompanyService.uploadLogo(file));
            refreshCompanyLogo();
            showToast(t('company.logoSaved'), 'success');
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setLogoBusy(false);
        }
    };
    const removeLogo = async () => {
        setLogoBusy(true);
        try {
            setCompany(await CompanyService.deleteLogo());
            refreshCompanyLogo();
        } finally {
            setLogoBusy(false);
        }
    };

    const field = (k) => {
        const props = {
            name: { required: true },
            email: { type: 'email' },
            website: { placeholder: 'www.geodezija.me' },
            bankAccount: { placeholder: '510-0000000000000-00', helperText: errors.bankAccount || t('company.bankHint') },
            cadastreReminderDays: { type: 'number', helperText: errors.cadastreReminderDays || t('company.reminderHint'), slotProps: { htmlInput: { min: 1, max: 365 } } },
        }[k] || {};
        return (
            <TextField key={k} fullWidth size="small" label={t(`company.fields.${k}`)} value={form[k] ?? ''} onChange={set(k)}
                       error={!!errors[k]} helperText={errors[k]} {...props} slotProps={{ inputLabel: { shrink: true }, ...props.slotProps }} />
        );
    };
    const editButton = (name) => (
        <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => open(name)}>{t('common.edit')}</Button>
    );

    return (
        <Layout>
            <PageHeader title={t('company.title')} subtitle={t('company.subtitle')} />
            {!company ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : (
                <Box sx={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Profile card */}
                    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '18px', p: { xs: 2.5, sm: 3.5 },
                                               background: 'linear-gradient(135deg, #FFFFFF 0%, #F4F8FB 100%)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, sm: 3.5 }, flexWrap: 'wrap' }}>
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Box sx={{
                                    width: 132, height: 96, borderRadius: '16px', bgcolor: '#fff', border: '1px solid #E2E8F0',
                                    display: 'grid', placeItems: 'center', overflow: 'hidden', boxShadow: '0 8px 20px -12px rgba(30,58,82,0.4)',
                                }}>
                                    {logoBusy ? <CircularProgress size={22} />
                                        : logo ? <Box component="img" src={logo} alt={company.name} sx={{ maxWidth: '86%', maxHeight: '80%', objectFit: 'contain' }} />
                                            : <ApartmentOutlinedIcon sx={{ fontSize: 36, color: '#CBD5E1' }} />}
                                </Box>
                                <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp"
                                       onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
                                <Tooltip title={company.hasLogo ? t('company.changeLogo') : t('company.addLogo')}>
                                    <IconButton size="small" onClick={() => fileRef.current?.click()} disabled={logoBusy} aria-label={t('company.changeLogo')} sx={{
                                        position: 'absolute', right: -8, bottom: -8, width: 32, height: 32, bgcolor: '#fff', border: '1px solid #E2E8F0',
                                        color: '#1e3a52', boxShadow: '0 2px 8px rgba(30,58,82,0.2)', '&:hover': { bgcolor: '#EEF4F8' },
                                    }}><PhotoCameraOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                                </Tooltip>
                                {company.hasLogo && (
                                    <Tooltip title={t('company.removeLogo')}>
                                        <IconButton size="small" onClick={removeLogo} disabled={logoBusy} aria-label={t('company.removeLogo')} sx={{
                                            position: 'absolute', left: -8, bottom: -8, width: 28, height: 28, bgcolor: '#fff', border: '1px solid #E2E8F0',
                                            color: '#94A3B8', '&:hover': { color: '#DC2626', bgcolor: '#fff' },
                                        }}><DeleteOutlineIcon sx={{ fontSize: 15 }} /></IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography sx={{ fontSize: 23, fontWeight: 800, color: '#0F2135', lineHeight: 1.2 }}>{company.name}</Typography>
                                {company.city && <Typography sx={{ fontSize: 13.5, color: '#64748B', mt: 0.5 }}>{company.city}</Typography>}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                    <ContactLine icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} value={company.email} href={company.email && `mailto:${company.email}`} />
                                    <ContactLine icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />} value={company.phone} href={company.phone && `tel:${company.phone}`} />
                                    <ContactLine icon={<LanguageOutlinedIcon sx={{ fontSize: 18 }} />} value={company.website}
                                                 href={company.website && (/^https?:\/\//.test(company.website) ? company.website : `https://${company.website}`)} />
                                    {!company.email && !company.phone && !company.website && (
                                        <Typography sx={{ fontSize: 13.5, color: '#94A3B8' }}>{t('company.noContact')}</Typography>
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 2 }}>{t('company.logoHint')}</Typography>
                            </Box>
                            <Button variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => open('profile')} sx={{ alignSelf: 'flex-start', bgcolor: '#fff' }}>
                                {t('common.edit')}
                            </Button>
                        </Box>
                    </Paper>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5, alignItems: 'start' }}>
                        <SectionCard title={<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PlaceOutlinedIcon sx={{ fontSize: 18, color: '#427898' }} />{t('company.sections.details')}</Box>}
                                     action={editButton('details')}>
                            <Box sx={{ px: 2.5, py: 1 }}>
                                <InfoRow label={t('company.fields.address')}>{company.address}</InfoRow>
                                <InfoRow label={t('company.fields.city')}>{company.city}</InfoRow>
                                <InfoRow label={t('company.fields.taxNumber')}>{company.taxNumber}</InfoRow>
                            </Box>
                        </SectionCard>
                        <SectionCard title={<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><HourglassTopIcon sx={{ fontSize: 18, color: '#B45309' }} />{t('company.sections.cadastre')}</Box>}
                                     action={editButton('cadastre')}>
                            <Box sx={{ px: 2.5, py: 1 }}>
                                <InfoRow label={t('company.fields.cadastreReminderDays')}>{t('company.daysValue', { count: company.cadastreReminderDays })}</InfoRow>
                                <Typography sx={{ fontSize: 12, color: '#94A3B8', py: 1 }}>{t('company.reminderHint')}</Typography>
                            </Box>
                        </SectionCard>
                        {/* Printed only on pro-forma invoices, so the client knows where to pay. */}
                        <SectionCard title={<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalanceOutlinedIcon sx={{ fontSize: 18, color: '#427898' }} />{t('company.sections.bank')}</Box>}
                                     action={editButton('bank')}>
                            <Box sx={{ px: 2.5, py: 1 }}>
                                <InfoRow label={t('company.fields.bankName')}>{company.bankName}</InfoRow>
                                <InfoRow label={t('company.fields.bankAccount')}>{company.bankAccount}</InfoRow>
                                <Typography sx={{ fontSize: 12, color: '#94A3B8', py: 1 }}>{t('company.bankHint')}</Typography>
                            </Box>
                        </SectionCard>
                    </Box>
                </Box>
            )}

            <Drawer anchor="right" open={!!panel} onClose={saving ? undefined : () => setPanel(null)}
                    PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #EEF2F6' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1a2332' }}>{panel && t(`company.edit.${panel}`)}</Typography>
                    <IconButton onClick={() => setPanel(null)} disabled={saving} aria-label={t('common.close')}><CloseIcon /></IconButton>
                </Box>
                <Box component="form" id="company-form" onSubmit={save} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {panel && SECTIONS[panel].map(field)}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, py: 2, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC' }}>
                    <Button onClick={() => setPanel(null)} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                    <Button type="submit" form="company-form" variant="contained" disableElevation disabled={saving} sx={{ minWidth: 120 }}>
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('common.save')}
                    </Button>
                </Box>
            </Drawer>
        </Layout>
    );
}
